import { useEffect, useRef, useState } from "react";
import { Share2, Check, Link2, MessageCircle, Facebook, Smartphone } from "lucide-react";
import { API } from "../lib/api";
import { track } from "../lib/analytics";

const XIcon = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Reusable "Share this" popover. Hands out backend smart links so WhatsApp/iMessage/Facebook render rich cards.
export default function ShareMenu({ kind = "music", slug, title, text, releaseId, eventType = "share_song", className = "", label = "Share" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const shareUrl = (channel) => `${API}/s/${kind}/${slug}?utm_source=${channel}&utm_medium=share&utm_campaign=${encodeURIComponent(slug)}`;
  const shareText = text || `${title} — DJ Código`;
  const fire = (channel) => track(eventType, { releaseId, metadata: { channel, kind, slug } });

  const copy = async () => {
    fire("copy");
    try { await navigator.clipboard.writeText(shareUrl("copy")); setCopied(true); setTimeout(() => { setCopied(false); setOpen(false); }, 1400); } catch {}
  };
  const native = async () => {
    fire("native");
    try { await navigator.share({ title: `${title} — DJ Código`, text: shareText, url: shareUrl("native") }); setOpen(false); } catch {}
  };
  const external = (channel, href) => { fire(channel); window.open(href, "_blank", "noopener,noreferrer"); setOpen(false); };

  const items = [
    { id: "copy", label: copied ? "Copied!" : "Copy Link", Icon: copied ? Check : Link2, onClick: copy },
    ...(navigator.share ? [{ id: "native", label: "Share…", Icon: Smartphone, onClick: native }] : []),
    { id: "whatsapp", label: "WhatsApp", Icon: MessageCircle, onClick: () => external("whatsapp", `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl("whatsapp")}`)}`) },
    { id: "facebook", label: "Facebook", Icon: Facebook, onClick: () => external("facebook", `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl("facebook"))}`) },
    { id: "x", label: "X", Icon: XIcon, onClick: () => external("x", `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl("x"))}`) },
  ];

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        data-testid="share-button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.03] px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-white/80 transition-transform active:scale-95 hover:border-gold/40"
      >
        <Share2 size={14} /> {label}
      </button>
      {open && (
        <div data-testid="share-menu" role="menu" className="glass absolute left-0 top-full z-40 mt-2 w-48 overflow-hidden rounded-2xl border border-white/10 bg-ink/95 p-1.5 shadow-2xl">
          {items.map(({ id, label: l, Icon, onClick }) => (
            <button key={id} data-testid={`share-${id}`} role="menuitem" onClick={onClick} className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm text-white/80 transition-colors hover:bg-white/5 hover:text-gold">
              <Icon size={14} /> {l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
